// =============================================================================
// Pure helpers for the Room Tracker board.
//
// Everything here is derived from (startedAt, durationMinutes) and a corrected
// clock. Nothing is stored, and nothing here talks to the network — the board
// polls every couple of seconds but ticks every second, so these run far more
// often than any request does.
// =============================================================================
import type { BookingItem } from './api';

export type BookingState = 'running' | 'warning' | 'expired';

/** Overdue by more than this and the booking is treated as forgotten, not late. */
export const STALE_OVERTIME_MS = 2 * 60 * 60 * 1000;

export function stateOf(booking: BookingItem, nowMs: number, warningMinutes: number): BookingState {
  const expiresAt = Date.parse(booking.expiresAt);
  if (nowMs >= expiresAt) return 'expired';
  if (nowMs >= expiresAt - warningMinutes * 60_000) return 'warning';
  return 'running';
}

/** Milliseconds until expiry; negative once a booking is running over. */
export function msRemaining(booking: BookingItem, nowMs: number): number {
  return Date.parse(booking.expiresAt) - nowMs;
}

/**
 * "42:07", or "1:42:07" once past an hour. Always the absolute value — the sign
 * is carried by the caller as a "+" prefix and a colour, which reads better at
 * a glance across a room than a minus sign does.
 */
export function formatDuration(ms: number): string {
  const total = Math.floor(Math.abs(ms) / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * Blend a new clock-offset sample into the running one.
 *
 * The countdown is derived on the client, so a terminal whose clock is four
 * minutes fast would alarm four minutes early on a room that is still running.
 * Each poll measures the offset against the server, but those samples jitter
 * with network latency, and overwriting on every one makes the displayed
 * seconds stutter. So: smooth ordinary samples, and jump immediately on a large
 * step, which is a clock actually being changed rather than noise.
 */
export function blendOffset(previous: number | null, sample: number): number {
  if (previous === null) return sample;
  if (Math.abs(sample - previous) > 5_000) return sample;
  return previous + (sample - previous) * 0.2;
}

/**
 * One offset measurement, corrected for the round trip.
 *
 * serverNow was true when the server wrote it, which is roughly half a round
 * trip before the response landed. Ignoring that biases every terminal's clock
 * slow by the latency — small, but free to remove.
 */
export function measureOffset(serverNowIso: string, sentAtMs: number, receivedAtMs: number): number {
  const oneWay = (receivedAtMs - sentAtMs) / 2;
  return Date.parse(serverNowIso) + oneWay - receivedAtMs;
}

export interface RoomGroup {
  roomId: string;
  roomNumber: string;
  roomDisplayName: string | null;
  bookings: BookingItem[];
}

/**
 * Group live bookings by room, keeping every room from the rooms list — a room
 * with nothing in it still has to be on the board, because that is the one you
 * click to start a booking.
 */
export function groupByRoom(
  rooms: { id: string; number: string; displayName: string | null }[],
  bookings: BookingItem[],
): RoomGroup[] {
  const byRoom = new Map<string, BookingItem[]>();
  for (const s of bookings) {
    const list = byRoom.get(s.roomId);
    if (list) list.push(s);
    else byRoom.set(s.roomId, [s]);
  }
  return rooms.map((r) => ({
    roomId: r.id,
    roomNumber: r.number,
    roomDisplayName: r.displayName,
    bookings: byRoom.get(r.id) ?? [],
  }));
}

/**
 * Room Tracker's ordering.
 *
 * Rooms with a trackerSortOrder come first, in that order. Rooms without one
 * follow, keeping the order the API already returned them in — which is Room
 * Plan's. That fallback is the point: a room nobody has placed yet drops to the
 * end of the board rather than vanishing from it, so forgetting to order a new
 * room is a cosmetic problem and never a missing room.
 *
 * Room Plan is unaffected; it never reads the field.
 */
export function sortRoomsForTracker<T extends { id: string; trackerSortOrder?: number | null }>(
  rooms: T[],
): T[] {
  const apiOrder = new Map(rooms.map((r, i) => [r.id, i]));
  return [...rooms].sort((a, b) => {
    const ao = a.trackerSortOrder ?? null;
    const bo = b.trackerSortOrder ?? null;
    if (ao !== null && bo !== null) return ao - bo;
    if (ao !== null) return -1;
    if (bo !== null) return 1;
    return (apiOrder.get(a.id) ?? 0) - (apiOrder.get(b.id) ?? 0);
  });
}
